import React, { useEffect, useMemo } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createEnrollment,
  updateEnrollment,
} from "../redux/actions/enrollmentActions";
import { getStudents } from "../redux/actions/studentActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { getClassrooms } from "../redux/actions/classroomActions";
import { enrollmentActions } from "../redux/slices/enrollment";
import { cleanFormData } from "../helpers/formHelpers";

import { EnrollmentStatus } from "../shared/enums/EnrollmentStatus";
import { EnrollmentResponseDto } from "../shared/dtos/enrollment/EnrollmentResponseDto";
import { EnrollmentRequestDto } from "../shared/dtos/enrollment/EnrollmentRequestDto";
import { EnrollmentUpdateRequestDto } from "../shared/dtos/enrollment/EnrollmentUpdateRequestDto";

const statusOptions = Object.values(EnrollmentStatus).map((status) => ({
  value: status,
  label: status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " "),
}));

const createEnrollmentSchema = Yup.object().shape({
  studentId: Yup.string().required("Student selection is required"),
  academicYearId: Yup.string().required("Academic Year is required"),
  classroomId: Yup.string().required("Classroom is required"),
});

const updateEnrollmentSchema = Yup.object().shape({
  classroomId: Yup.string().required("Classroom is required"),
  status: Yup.mixed<EnrollmentStatus>()
    .oneOf(Object.values(EnrollmentStatus))
    .required("Status is required"),
  withdrawalDate: Yup.date()
    .nullable()
    .when("status", {
      is: EnrollmentStatus.WITHDRAWN,
      then: (schema) =>
        schema.required("Withdrawal date is required when status is Withdrawn"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
});

interface CreateOrUpdateEnrollmentFormProps {
  enrollment?: EnrollmentResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

interface FormValues {
  studentId: string;
  academicYearId: string;
  classroomId: string;
  status: EnrollmentStatus;
  withdrawalDate: string;
}

const CreateOrUpdateEnrollmentForm: React.FC<
  CreateOrUpdateEnrollmentFormProps
> = ({
  enrollment = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const { items: students } = useAppSelector((state) => state.student);
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );
  const { items: classrooms } = useAppSelector((state) => state.classroom);

  useEffect(() => {
    if (!isEditing && (!students || students.length === 0)) {
      dispatch(getStudents());
    }
    if (!isEditing && (!academicYears || academicYears.length === 0)) {
      dispatch(getAcademicYears());
    }
    if (!classrooms || classrooms.length === 0) {
      dispatch(getClassrooms());
    }
  }, [dispatch, isEditing, students, academicYears, classrooms]);

  const studentOptions = useMemo(
    () =>
      students?.map((s) => ({
        value: s.id,
        label: `${s.firstName} ${s.lastName} (${s.studentId || "No ID"})`,
      })) || [],
    [students],
  );

  const yearOptions = useMemo(
    () => academicYears?.map((y) => ({ value: y.id, label: y.name })) || [],
    [academicYears],
  );

  const classroomOptions = useMemo(
    () => classrooms?.map((c) => ({ value: c.id, label: c.name })) || [],
    [classrooms],
  );

  const initialValues: FormValues = {
    studentId: enrollment?.studentId || "",
    academicYearId: "",
    classroomId: enrollment?.classroomId || "",
    status: enrollment?.status || EnrollmentStatus.PENDING,
    withdrawalDate: "",
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(enrollmentActions.setErrorUpdate(null))
            : () => dispatch(enrollmentActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={
          isEditing ? updateEnrollmentSchema : createEnrollmentSchema
        }
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          if (isEditing && enrollment) {
            const updatePayload: EnrollmentUpdateRequestDto = {
              classroomId: rawCleaned.classroomId,
              status: values.status,
              withdrawalDate:
                values.status === EnrollmentStatus.WITHDRAWN
                  ? rawCleaned.withdrawalDate || null
                  : null,
            };

            dispatch(
              updateEnrollment({ id: enrollment.id, values: updatePayload }),
            );
          } else {
            const createPayload: EnrollmentRequestDto = {
              studentId: rawCleaned.studentId,
              academicYearId: rawCleaned.academicYearId,
              classroomId: rawCleaned.classroomId,
            };

            dispatch(createEnrollment(createPayload));
          }
        }}
      >
        {({ dirty, isValid, values }: FormikProps<FormValues>) => (
          <Form>
            <Stack spacing={2}>
              {!isEditing && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      textAlign: "left",
                      fontWeight: 700,
                    }}
                  >
                    Initial Enrollment Details
                  </Typography>

                  <FormSelectField
                    name="studentId"
                    label="Select Student"
                    options={studentOptions}
                  />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <FormSelectField
                      name="academicYearId"
                      label="Academic Year"
                      options={yearOptions}
                    />
                    <FormSelectField
                      name="classroomId"
                      label="Classroom Allocation"
                      options={classroomOptions}
                    />
                  </Box>
                </>
              )}

              {isEditing && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      textAlign: "left",
                      fontWeight: 700,
                    }}
                  >
                    Update Enrollment
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <FormSelectField
                      name="classroomId"
                      label="Classroom Allocation"
                      options={classroomOptions}
                    />
                    <FormSelectField
                      name="status"
                      label="Enrollment Status"
                      options={statusOptions}
                    />
                  </Box>

                  {values.status === EnrollmentStatus.WITHDRAWN && (
                    <FormTextField
                      name="withdrawalDate"
                      label="Withdrawal Date"
                      type="date"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                </>
              )}

              <SubmitButton
                title={isEditing ? "Save Changes" : "Enroll Student"}
                loadingText={isEditing ? "Saving..." : "Enrolling..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <PersonAddOutlinedIcon />
                }
                sx={{ mt: 2 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateEnrollmentForm;
